import { useEffect, useRef } from "react";
import { isEmpty, isNil } from "lodash";
import { CellSpanModule, ModuleRegistry } from "ag-grid-community";

import "@plannel/pages/factory-plan/common/style/grid.scss";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import DataState from "@plannel/components/aggrid/DataState";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import FilterStatusPanel from "@plannel/components/aggrid/FilterStatusPanel";
import fpCommonService from "@plannel/services/fp/fp-common-service";
import dateUtils, { DEFAULT_DATE_FORMAT } from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

// CellSpanModule(ag-grid-community) 전역 등록 — old RealGrid mergeRule(동일값 세로 병합) 대응으로
// FP Report 화면 일부(ResourceUtilization.js/PsiDetail.js 등)가 enableCellSpan+spanRows를 쓰는데,
// 이 모듈은 AllEnterpriseModule 번들에 없어 별도 등록이 필요하다. 앱 전역 진입점(index.js)이 아니라
// FP 화면이 공통으로 거치는 이 파일에서 등록한다(2026-08-11 사용자 지시 — 앱 전체 부트스트랩
// 파일 대신 FP 스코프 공용 파일로 국한). ModuleRegistry.registerModules는 멱등이라 여러 번 호출돼도
// 안전하고, 그리드가 렌더되기 전(이 파일을 import하는 시점)에 항상 먼저 실행된다.
ModuleRegistry.registerModules([CellSpanModule]);

// old FP 마스터 화면의 datetime 편집 컬럼(editor type:"date", "yyyy-MM-dd HH:mm:ss") 대응.
// 공용 날짜 셀 에디터 components/GridDatePicker.js는 dateType:"time"이어도 onChange가 항상
// DEFAULT_DATE_FORMAT(="yyyy-MM-dd")로 포맷해 시:분을 버리는 버그가 있어(GridDatePicker.js:53-68,
// 코드베이스 전체 dateType:"time" 사용처 0건) 쓸 수 없다 — 대신 AG Grid 내장 agDateStringCellEditor +
// includeTime:true(브라우저 네이티브 datetime-local, 캘린더+시각 선택 모두 지원)를 쓴다.
// 이 에디터가 커밋하는 값이 이미 "YYYY-MM-DDTHH:mm:ss(Z)" 형식(아래 dataTypeDefinitions의
// dateFormatter가 만듦)이라 별도 파싱이 필요 없지만, 클립보드 붙여넣기 등 valueParser 경로
// (직접 편집이 아닌 경로)를 대비해 포맷 검증은 남겨둔다: "YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss",
// ISO("YYYY-MM-DDTHH:mm:ss") 또는 끝에 "Z"가 붙은 형태를 받아 정규화한다. 끝의 "Z"는 그대로
// 보존해야 한다 — 지워버리면 dataTypeDefinitions.dateFormatter가 만든 값(캘린더로 편집한 값)이
// 이 정규식/포맷 불일치로 거부되어 params.oldValue로 조용히 되돌려진다(편집이 셀에 반영 안 되는
// 버그로 나타남). 형식이 틀리면 이전 값 유지, 빈 입력은 null.
const DATETIME_INPUT_REGEX = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}(?::\d{2})?))?(Z)?$/;

export const parseDateTimeCellInput = (params) => {
  const raw = (params.newValue ?? "").toString().trim();
  if (raw === "") return null;

  const match = DATETIME_INPUT_REGEX.exec(raw);
  if (!match) return params.oldValue;

  const time = match[2] ? (match[2].length === 5 ? `${match[2]}:00` : match[2]) : "00:00:00";
  const zone = match[3] ?? "";
  const normalized = `${match[1]}T${time}${zone}`;

  return Number.isNaN(new Date(normalized).getTime()) ? params.oldValue : normalized;
};

// FP 화면 전용 그리드 설정 — 공용 components/aggrid/DefaultGridSetting.js는 건드리지 않고,
// FP만 쓰는 columnType을 여기서 얹어 확장한다. 사용법은 DefaultGridSetting과 동일.
const FpGridSetting = (options) => {
  const base = DefaultGridSetting(options);

  return {
    ...base,
    // ⚠️ 백엔드 LocalDateTimeSerializer가 naive LocalDateTime에 "Z"를 잘못 붙이는 버그(백엔드
    // backend-gaps.md 등재 필요)에 맞춰 프론트를 대칭적으로 구성한 것 — 백엔드가 이 버그를
    // 고쳐서 "Z" 없이 순수 로컬 값을 내려주게 되면, 이 dataTypeDefinitions도 함께 제거해야 한다
    // (안 그러면 그때는 진짜 naive 값에 불필요한 +9h 변환이 걸려서 반대 방향 버그가 생긴다).
    // dateParser: raw "...Z" 문자열 → Date(진짜 UTC 인스턴트). 네이티브 datetime-local 입력이
    // 이 Date를 "로컬" getter로 읽어서 보여주므로 그리드 셀 표시(dateUtils.formatDateTime)와
    // 편집기 초깃값이 항상 일치한다.
    // dateFormatter: 편집기가 커밋한 Date(로컬로 해석된 값) → 문자열. 반드시 "UTC" getter로
    // 읽어서 되돌려야 dateParser와 왕복이 정확히 상쇄된다(로컬 getter를 쓰면 편집할 때마다
    // 값이 계속 밀린다).
    dataTypeDefinitions: {
      dateTimeString: {
        baseDataType: "dateTimeString",
        extendsDataType: "dateTimeString",
        dateParser: (value) => {
          if (!value) return undefined;
          const d = new Date(value);
          return Number.isNaN(d.getTime()) ? undefined : d;
        },
        dateFormatter: (date) => {
          if (!date) return undefined;
          const pad = n => String(n).padStart(2, "0");
          return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
                 `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}Z`;
        },
      },
    },
    columnTypes: {
      ...base.columnTypes,
      // AG Grid 기본 제공은 rightAligned/numericColumn뿐, center 정렬 내장 타입이 없어 FP에서 추가.
      // old textAlignment: "center" 대응 — type 배열에 "centerAligned"를 얹어서 사용.
      centerAligned: {
        cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
      },
      // 편집 가능한 일시 컬럼(위 parseDateTimeCellInput 참고). agDateStringCellEditor +
      // cellEditorParams.includeTime:true는 에디터 UI(시:분 선택 노출)만 켜는 옵션이고,
      // 값을 읽고/쓰는 파싱·포맷은 cellDataType이 결정한다 — cellDataType:"dateTimeString"을
      // 명시해야 "YYYY-MM-DDTHH:mm:ss(Z)" 전체를 인식하고, 위 dataTypeDefinitions의
      // dateParser/dateFormatter가 이 컬럼에 적용된다.
      dateTimeColumn: {
        cellDataType: "dateTimeString",
        valueFormatter: dateUtils.formatDateTime,
        valueParser: parseDateTimeCellInput,
        cellEditor: "agDateStringCellEditor",
        cellEditorParams: { includeTime: true },
      },
      // 편집 가능한 날짜 전용(시간 없음) 컬럼 — 백엔드 LocalDate 대응. dateTimeColumn과 달리
      // UTC 변환이 필요 없어(시간 자체가 없음) cellDataType/dataTypeDefinitions 없이 AG Grid
      // 기본 dateString 추론 그대로 쓴다. agDateStringCellEditor가 커밋하는 "YYYY-MM-DD" 문자열이
      // 곧 LocalDate 직렬화 형식이라 valueParser도 불필요.
      dateColumn: {
        cellEditor: "agDateStringCellEditor",
        valueFormatter: (p) => dateUtils.formatDate(p.value, DEFAULT_DATE_FORMAT),
      },
    },
  };
};

// §8 공통코드 콤보 — fpCommonService.getCodes(grpCd)를 로드해 seq 정렬된 값 배열을 ref로 보관.
// cellEditorParams는 편집 시점에 최신값을 읽어야 해서(그리드가 컬럼을 초기 캡처) ref로 반환.
export const useCommonCodeValues = (grpCd) => {
  const valuesRef = useRef([]);

  useEffect(() => {
    fpCommonService.getCodes(grpCd)
      .then(response => {
        valuesRef.current = (response.data ?? [])
          .slice()
          .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
          .map(c => c.comnCd);
      })
      .catch(e => debug(e));
  }, [grpCd]);

  return valuesRef;
};

// §8 공통코드 콤보 — agRichSelectCellEditor의 cellEditorParams. 목록 항목은 "코드 - 명칭"(명칭은
// i18n `${grpCd}_${코드}` 키, 없으면 코드만). 그리드 셀(비편집 상태) 표시엔 영향 없음(raw value).
export const codeComboCellEditorParams = (grpCd, valuesRef, t) => ({
  values: valuesRef.current,
  cellRenderer: (params) => {
    const value = params?.value;
    if (isEmpty(value)) return "";
    const key = `${grpCd}_${value}`;
    const label = t(key);
    return label && label !== key ? `${value} - ${label}` : value;
  },
});

// §9-2 컬럼 필수값(required) 검증 — old RealGrid `validRules: [{ criteria: "required" }]` 대응.
// qty=0/activeYn=false 같은 유효한 falsy 값을 빈 값으로 오판하지 않도록 lodash isEmpty가 아니라
// isNil(null/undefined)+빈 문자열만 위반으로 본다.
const isBlankValue = (value) => isNil(value) || value === "";

// 저장 대상(추가/변경된 행)에서 비어 있는 필수값을 전부 찾는다.
// requiredFields: [{ field, label }, ...] — 화면마다 다르므로 호출부에서 REQUIRED_FIELDS로 선언.
// DataState.getAllStateRow가 주는 created+updated 행 id만 검사한다(저장 대상 자체가 변경행뿐이라,
// old의 validateCells가 그리드 전체를 검사하는 것과 실질적으로 동등).
export const findMissingRequired = (api, requiredFields) => {
  const stateRows = DataState.getAllStateRow(api);
  const rowIds = [...(stateRows?.created ?? []), ...(stateRows?.updated ?? [])];
  const missing = [];

  for (const rowId of rowIds) {
    const node = api.getRowNode(rowId);
    if (!node) continue;

    for (const required of requiredFields) {
      if (isBlankValue(node.data?.[required.field])) {
        missing.push({ ...required, rowIndex: node.rowIndex });
      }
    }
  }

  return missing;
};

// 문제 셀로 스크롤 + 포커스 — old의 "[Row: n Column: 헤더]" 지목을 스낵바 한 줄로는 그대로 못
// 재현하므로, 첫 위반 셀로 스크롤+포커스하는 것으로 절충한다. 컬럼설정에서 숨긴 컬럼이면
// 포커스는 건너뛴다(행만 보이게 스크롤).
export const focusInvalidCell = (api, { rowIndex, field }) => {
  if (isNil(rowIndex)) return;

  api.ensureIndexVisible(rowIndex);

  if (api.getColumn(field)?.isVisible()) {
    api.ensureColumnVisible(field);
    api.setFocusedCell(rowIndex, field);
  }
};

// ── Report 화면(그리드+차트, 행 그룹형) 공통 함수 — fp-report-screen-guide.md §A-6b 계열 ──
// PlanResult.js(§A-6b golden reference)를 시작으로 factory-plan/report의 활성 화면 14개가
// 전부 이 모양(행 그룹 트리 + CustomDetailHeader 코드/명칭 토글 + FilterStatusPanel 브레드크럼)을
// 쓰고 있어서 각 화면에 복붙돼 있던 함수를 여기로 뺐다(2026-08-10). 새 Report 화면을 만들 때는
// 이 섹션의 함수를 먼저 검토하고, 화면 고유 로직만 화면에 남긴다.

// 코드 컬럼 헤더 화살표로 짝 명칭 컬럼을 표시/숨김한 상태를 redux viewState에서 읽는다.
// CustomDetailHeader는 마운트 시 이 값으로 초기 표시 상태를 잡는다(true=명칭 숨김, 기본값).
export const readHide = (reduxViewState, key) => (
  reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, key) ? reduxViewState[key] : true
);

// 'SUM' 등 합계 행의 코드/명칭 셀을 번역해서 보여준다(old displayCallback 대응).
export const createSumAwareFormatter = (sumRowValue, t) => (params) => (
  params.value === sumRowValue ? t(sumRowValue) : params.value
);

// 행 그룹(트리) 표시 컬럼(코드+화살표로 열리는 명칭) 한 쌍 — old mergeRule 컬럼을 Row Grouping으로
// 재현하는 패턴의 화면 표시부. hidden(readHide로 미리 계산)/valueFormatter(보통
// createSumAwareFormatter의 결과)는 호출부가 넘긴다. 코드/명칭 폭이 다른 화면(예:
// ProdSalesStockStatus)은 cdWidth/nmWidth로 따로 지정 — 없으면 둘 다 width를 쓴다. cellStyle은
// 특정 레벨만 강조가 필요한 화면(예: ResProdPlan의 자원 과부하 강조)에서만 넘긴다. extraDetailColumns는
// 명칭 컬럼 외에 같은 토글에 같이 접혔다 펼쳐졌으면 하는 컬럼의 colId를 추가로 넘긴다(예:
// PsiDetail의 인벤토리 토글에 품목구분을 같이 묶는 경우) — 그 컬럼들의 초기 hide 상태와 colId는
// 호출부가 별도로 맞춰줘야 한다(이 헬퍼가 만드는 컬럼이 아니므로).
export const buildGroupColumnPair = ({
  viewName, hidden, valueFormatter, cdColId, cdField, cdHeader, nmColId, nmField, nmHeader, hideKey,
  width, cdWidth = width, nmWidth = width, cellStyle, extraDetailColumns = []
}) => [
  {
    headerName: cdHeader, colId: cdColId, showRowGroup: cdField, width: cdWidth, pinned: "left", lockPinned: true,
    type: ["nonEditableColumn"], cellRenderer: "agGroupCellRenderer",
    cellRendererParams: { suppressCount: true }, valueFormatter, cellStyle,
    headerComponent: CustomDetailHeader,
    headerComponentParams: { viewName, colsHide: hidden, reduxKey: hideKey, detailColumns: [nmColId, ...extraDetailColumns] }
  },
  {
    headerName: nmHeader, colId: nmColId, showRowGroup: nmField, width: nmWidth, pinned: "left", lockPinned: true,
    hide: hidden, type: ["nonEditableColumn"], cellRenderer: "agGroupCellRenderer",
    cellRendererParams: { suppressCount: true }, valueFormatter, cellStyle
  }
];

// 행 그룹(트리) 그리드의 표준 3종 옵션 — groupDisplayType:"custom"(레벨마다 자기 컬럼 자리에서
// 펼침/접힘 아이콘), groupDefaultExpanded:-1(전부 펼친 채 시작), groupHideOpenParents(펼쳐진
// 그룹의 부모 행을 감춰 old의 셀 병합과 같은 모양+접기를 동시에 구현). defaultGridMemo에 스프레드.
export const REPORT_TREE_GRID_OPTIONS = {
  groupDisplayType: "custom",
  groupDefaultExpanded: -1,
  groupHideOpenParents: true,
};

// 하단 상태바 — 왼쪽은 FilterStatusPanel(클릭/필터로 선택한 위치 브레드크럼, old의 차트 위
// 타이틀 텍스트를 대체), 가운데는 AG Grid 내장 전체 행 수, 오른쪽은 내장 집계(셀 범위 선택 시
// 합계/평균 등). Report 화면 전반이 쓰는 동일 3종 구성.
export const buildReportStatusBar = (viewName) => ({
  statusPanels: [
    { statusPanel: FilterStatusPanel, statusPanelParams: { viewName }, align: "left", key: "filterStatusPanel" },
    { statusPanel: "agTotalRowCountComponent", align: "center" },
    { statusPanel: "agAggregationComponent" }
  ]
});

// 평평한 목록(크로스탭 아님, 클릭 드릴다운/차트 연동이 없는) Report 화면용 — FilterStatusPanel(왼쪽
// 브레드크럼)은 그리드 클릭으로 채워지는 값이 있어야 의미가 있는데, 이 화면들은 채우는 코드 자체가
// 없어 항상 빈 칩 자리로 남는다(2026-08-12 발견). MP `MrpOrderList.js`/`OrderTracking.js`,
// RP `RpDemand.js` 등 다른 모듈의 평평한 그리드 화면도 FilterStatusPanel 없이 Total Rows만 쓴다 —
// 그 관례를 따른다.
export const buildFlatReportStatusBar = () => ({
  statusPanels: [
    { statusPanel: "agTotalRowCountComponent", align: "center" },
    { statusPanel: "agAggregationComponent" }
  ]
});

// 조회할 때마다 피벗 컬럼 구성 자체가 바뀌는 그리드에서, 공용 GridUtils.setColumnDefs가 새
// columnDefs를 적용하기 직전 컬럼 상태를 캡처해 적용 후 되돌리는 것(GridUtils.js, initState:false
// 경로) 때문에 "이전 조회의 컬럼 순서"가 새 순서를 덮어쓰는 문제를 고친다 — 백엔드가 준
// crosstabHeader 순서를 그대로 다시 못박는다. colId만 담으면 순서 외 속성(hide/width/pinned 등)은
// 건드리지 않는다(AG Grid 35: 값이 undefined인 속성은 "변경하지 않음").
export const applyColumnOrder = (api, columnDefs) => {
  const state = [];
  const collect = (defs) => {
    for (const def of defs ?? []) {
      if (def.children) collect(def.children);
      else state.push({ colId: def.colId ?? def.field });
    }
  };
  collect(columnDefs);

  if (state.length > 0) api.applyColumnState({ state, applyOrder: true });
};

// 일버킷일 때 자식 헤더로 오는 요일 코드(old DashBoardParameter의 DayOfWeek→"MON".."SUN") —
// old setColumnColorOnSunSat(grid.js) 대응, 토=blue-text/일=red-text, 평일은 클래스 없음.
export const DAY_OF_WEEK_CODES = new Set(["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"]);

// colDef.headerClass를 직접 지정하면 type(["numberColumn","rightAligned"])이 자동으로 주는
// "ag-right-aligned-header"를 덮어써서 없어진다(합쳐지지 않음) — 토/일만 헤더 메뉴 아이콘
// 위치·정렬이 달라지는 버그가 실제로 있었다(2026-08-10). 색상 클래스와 함께 명시적으로 넣어준다.
export const dayOfWeekHeaderClass = (code) => {
  if (code === "SUN") return ["ag-right-aligned-header", "red-text"];
  if (code === "SAT") return ["ag-right-aligned-header", "blue-text"];
  return undefined;
};

// crosstabHeader("2026-07-20_W30" 또는 일버킷일 때 "2026-07-20_MON")를 "_" 앞(날짜)=그룹,
// 뒤(주차/월/요일)=자식으로 묶는다. 그룹 헤더(날짜)는 동적 문자열이라 i18n 대상이 아니지만,
// 자식 헤더가 요일 코드(MON..SUN)면 old처럼 다국어 처리 + 토/일 강조색을 적용한다(§A-6d).
// 다른 버킷(W30, 월 등)의 자식 헤더는 여전히 동적 문자열이라 그대로 둔다 — GridUtils.getColumnDefs가
// i18n.exists() 미매칭 시 원본을 그대로 두므로 별도 처리가 필요 없다. childExtraProps(header)는
// 화면별 추가 컬럼 속성(예: 임계값 초과 강조 cellStyle)이 필요할 때만 넘긴다.
export const buildDayBucketPivotColumnDefs = (crosstabHeader, t, { childExtraProps } = {}) => {
  const groups = [];
  const groupIndex = new Map();

  for (const header of crosstabHeader ?? []) {
    const separatorIndex = header.indexOf("_");
    const groupKey = separatorIndex > -1 ? header.substring(0, separatorIndex) : header;
    const childLabel = separatorIndex > -1 ? header.substring(separatorIndex + 1) : header;
    const isDayOfWeek = DAY_OF_WEEK_CODES.has(childLabel);

    if (!groupIndex.has(groupKey)) {
      groupIndex.set(groupKey, groups.length);
      groups.push({ headerName: groupKey, groupId: `pivot_${groupKey}`, marryChildren: true, children: [] });
    }

    groups[groupIndex.get(groupKey)].children.push({
      headerName: isDayOfWeek ? t(childLabel.toLowerCase()) : childLabel,
      field: header, colId: header, width: 110,
      type: ["numberColumn", "rightAligned"],
      headerClass: isDayOfWeek ? dayOfWeekHeaderClass(childLabel) : undefined,
      ...(childExtraProps ? childExtraProps(header) : {})
    });
  }

  return groups;
};

// 조회 성공 직후 rows[0](또는 클릭한 셀) 기준 focus를 걸 때 쓰는 예약 소비 — setRowData(rows)는
// React state 갱신이라 그 자리에서 곧바로 api.setFocusedCell을 부르면 그리드가 새 rowData를 아직
// 반영하기 전일 수 있다(레이스). "포커스를 걸어야 할 대상"만 pendingFocusRef에 적어두고, 그리드가
// 실제로 새 rowData를 반영한 시점(onRowDataUpdated 이벤트)에 이 함수로 소비한다.
// setFocusedCell만으로는 키보드 포커스 아웃라인만 생기고, 클릭했을 때 보이는 "선택된 셀"
// 하이라이트(cellSelection range)는 안 생겨서 addCellRange를 같이 호출해야 한다(§A-7).
export const consumePendingFocus = (gridRef, pendingFocusRef) => {
  const pending = pendingFocusRef.current;
  if (!pending) return;
  pendingFocusRef.current = null;

  const api = gridRef.current?.api;
  if (!api) return;

  api.clearCellSelection();
  api.addCellRange({ rowStartIndex: pending.rowIndex, rowEndIndex: pending.rowIndex, columns: [pending.colId] });
  api.setFocusedCell(pending.rowIndex, pending.colId);
};

export default FpGridSetting;
